import { useEffect, useRef, useState } from "react";
import { Microphone, Translate } from "@phosphor-icons/react";

// 30-language input toggle used across NaturePulse submission forms. Sets the
// input's `lang` attribute (so mobile keyboards switch layout / autocorrect
// dictionaries) and, where the browser supports SpeechRecognition, exposes a
// mic button that dictates straight into the field in the chosen language.
export const INPUT_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "sv", label: "Svenska" },
  { code: "pl", label: "Polski" },
  { code: "ru", label: "Русский" },
  { code: "uk", label: "Українська" },
  { code: "tr", label: "Türkçe" },
  { code: "ar", label: "العربية" },
  { code: "fa", label: "فارسی" },
  { code: "he", label: "עברית" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "mr", label: "मराठी" },
  { code: "ur", label: "اردو" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ไทย" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "sw", label: "Kiswahili" },
  { code: "am", label: "አማርኛ" },
];

// Utility: read/write the last-chosen input language on the browser so the
// preference sticks across sessions.
const LS_KEY = "np.input.lang";

export function useInputLang(defaultCode = "en") {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return defaultCode;
    return window.localStorage.getItem(LS_KEY) || defaultCode;
  });
  const setLang = (code) => {
    setLangState(code);
    try { window.localStorage.setItem(LS_KEY, code); } catch { /* noop */ }
  };
  return [lang, setLang];
}

/**
 * A textarea/input drop-in that includes a language picker + mic dictation.
 *
 * Props:
 *   value / onChange — controlled value
 *   as               — "input" | "textarea" (default "textarea")
 *   placeholder      — placeholder text
 *   rows             — for textarea
 *   testid           — root data-testid
 *   className        — passed through to the field
 *   showMic          — enable dictation button (default true)
 *   defaultLang      — initial language (falls back to remembered value)
 */
export default function LangInput({
  value,
  onChange,
  as = "textarea",
  placeholder,
  rows = 3,
  testid = "lang-input",
  className = "",
  showMic = true,
  defaultLang,
  maxLength,
  required,
  ...rest
}) {
  const [lang, setLang] = useInputLang(defaultLang || "en");
  const [listening, setListening] = useState(false);
  const [open, setOpen] = useState(false);
  const recRef = useRef(null);

  const label = INPUT_LANGUAGES.find((l) => l.code === lang)?.label || lang;

  const canRecognise = typeof window !== "undefined" && (
    "SpeechRecognition" in window || "webkitSpeechRecognition" in window
  );

  const toggleMic = () => {
    if (!canRecognise) return;
    if (listening) { recRef.current?.stop(); return; }
    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new R();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    let base = value || "";
    rec.onresult = (ev) => {
      let interim = "";
      let final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += t;
        else interim += t;
      }
      const combined = (base + (final ? " " + final : "") + (interim ? " " + interim : "")).trim();
      onChange({ target: { value: combined } });
      if (final) base += " " + final;
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* noop */ } }, []);

  const commonProps = {
    value: value ?? "",
    onChange,
    placeholder,
    lang,
    dir: ["ar", "fa", "he", "ur"].includes(lang) ? "rtl" : "ltr",
    maxLength,
    required,
    className: `w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--np-brand,#B85F2A)] ${className}`,
    "data-testid": `${testid}-field`,
    ...rest,
  };

  return (
    <div className="relative" data-testid={testid}>
      {as === "input"
        ? <input {...commonProps} />
        : <textarea rows={rows} {...commonProps} />
      }
      <div className="absolute right-1.5 bottom-1.5 flex gap-1">
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testid}-lang-btn`}
            className="inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur border border-black/15 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#241209] hover:border-black/40"
            title="Change input language"
          >
            <Translate size={10} weight="fill" /> {lang.toUpperCase()}
          </button>
          {open && (
            <div
              // Single-line inputs open the menu DOWNWARD (below the field)
              // so it never covers the content above; textareas keep the
              // upward placement so they don't push page content further.
              className={
                (as === "input"
                  ? "absolute right-0 top-full mt-1 "
                  : "absolute right-0 bottom-full mb-1 ") +
                "max-h-64 w-44 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-lg py-1 z-50"
              }
              data-testid={`${testid}-lang-menu`}
            >
              {INPUT_LANGUAGES.map((l) => (
                <button
                  type="button"
                  key={l.code}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  data-testid={`${testid}-lang-${l.code}`}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F5EBDC] ${
                    lang === l.code ? "font-semibold text-[color:var(--np-brand,#B85F2A)]" : "text-[#241209]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {showMic && canRecognise && (
          <button
            type="button"
            onClick={toggleMic}
            data-testid={`${testid}-mic`}
            title={listening ? "Stop dictation" : "Dictate in this language"}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase transition ${
              listening
                ? "bg-red-600 text-white border-red-600 animate-pulse"
                : "bg-white/85 backdrop-blur border-black/15 text-[#241209] hover:border-black/40"
            }`}
          >
            <Microphone size={10} weight="fill" />
          </button>
        )}
      </div>
    </div>
  );
}
